import type { MemberDataFirestoreRecord, Supplier } from "@albertine/types";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { getLocale, Lib, ServiceLanguageMap } from "albertine-shared-web";
import classNames from "classnames";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router";
import {
    canBeSharedAsProactivity,
    canBeUsedForBooking,
    hasAllBoundingBoxParams,
    hasAllLocationParams,
} from "../../common/CatalogItem";
import type { Agent } from "../../common/types/Agent";
import type { Request } from "../../common/types/Request";
import { excludeUndefined } from "../../common/utils/array.util";
import { singularPlural } from "../../common/utils/time.formatted.util";
import { type CreateOneOffBookingInput, EventEmitter } from "../../EventEmitter";
import {
    useCatalogSearchUrlParams,
    useCatalogSelectionUrlParams,
    useComponentState,
} from "../../utils/customHook.util";
import { nameWithoutTitle } from "../../utils/members.util";
import routes, { type CatalogContext } from "../../utils/routes";
import { MenuButton, type MenuButtonOptions } from "../Common/Button/MenuButton";
import Button from "../Common/Buttons";
import { Flex } from "../Common/Flex";
import Icon from "../Common/Icon";
import InlineTextDelimiter from "../Common/InlineTextDelimiter";
import Label from "../Common/Label";
import Tooltip from "../Common/Tooltip";
import HandleClicksOutside from "../HandleClicksOutside";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import "./Catalog.css";
import { useGet } from "@albertine/api-client";
import type {
    CatalogItem as CatalogItemApi,
    CatalogItemFull,
    RecommendationItem,
    RequestRecommendationsResponseData,
    SearchCatalogItemsQuery,
} from "@albertine/types/openapi/lmt";
import { Button as UiButton } from "@/components/ui/button";
import { useClaims } from "../../utils/claims.util";
import CatalogFilters, { MinimizedCatalogFilters } from "./CatalogFilters";
import CatalogItemCreate from "./CatalogItemCreate";
import CatalogItemHeader from "./CatalogItemHeader";
import CatalogItemView from "./CatalogItemView";
import CatalogSearchResults from "./CatalogSearchResults";
import { getDistanceOrigin } from "./distanceOrigin.util";
import EditCatalogItem from "./EditCatalogItem";
import { useCatalogSearchResults } from "./hooks/useCatalogSearchResults";
import { MapView } from "./MapView/MapView";
import { RecommendationResults } from "./RecommendationResults";
import ViewCatalogItem from "./ViewCatalogItem";
import { WebItemDetail } from "./WebItemDetail";

const contextToTitleMap: Record<CatalogContext, string> = {
    browse: "Catalog",
    proactivity: "Share articles",
    service: "Add options",
    recommendations: "Recommended options",
};

const contextToCartButtonTitle: Record<CatalogContext, string> = {
    browse: "", // There should not be this option
    proactivity: "Add to chat",
    service: "Add to request",
    recommendations: "Add to request",
};

const contextToAddedToCartText: Record<CatalogContext, string> = {
    browse: "", // There should not be this option
    proactivity: "Added to chat",
    service: "Added to request",
    recommendations: "Added to request",
};

const contextToSuccessfulAddition = (
    item: CatalogItemApi,
): Record<CatalogContext, string> => ({
    browse: `"${item.name}" successfully created`,
    proactivity: `"${item.name}" successfully created and added to chat`,
    service: `"${item.name}" successfully created and added to request`,
    recommendations: `"${item.name}" successfully created and added to request`,
});

function isRequestContext(
    context: CatalogContext,
): context is "service" | "recommendations" {
    return context === "service" || context === "recommendations";
}

export function CartButton(props: {
    context: CatalogContext;
    currentItem: CatalogItemFull | undefined;
    cartItems: string[];
    onClick: () => void;
    member: MemberDataFirestoreRecord | undefined;
    tooltipPlacement?: "bottom-left" | "left";
}) {
    const {
        context,
        currentItem,
        cartItems,
        onClick,
        member,
        tooltipPlacement = "left",
    } = props;

    if (!currentItem || context === "browse" || !member) {
        return null;
    }

    const title = contextToCartButtonTitle[context];
    const addedText = contextToAddedToCartText[context];
    const memberServiceLangugage = getLocale(member?.serviceLanguage);
    const canAddToCart =
        context === "proactivity"
            ? canBeSharedAsProactivity(currentItem, memberServiceLangugage)
            : isRequestContext(context)
              ? canBeUsedForBooking(currentItem, memberServiceLangugage)
              : false;

    const tooltipMessage = `Please provide the name, image, description, and ${ServiceLanguageMap[memberServiceLangugage]} translation`;

    const button = canAddToCart ? (
        <UiButton type="button" onClick={onClick}>
            {title}
        </UiButton>
    ) : (
        <Tooltip label={tooltipMessage} placement={tooltipPlacement}>
            <UiButton type="button" onClick={onClick} disabled={!canAddToCart}>
                {title}
            </UiButton>
        </Tooltip>
    );

    // Bookings reference catalog items by UUID; legacy bookings may still hold the lmtId.
    const isInCart =
        cartItems.includes(currentItem.id) || cartItems.includes(currentItem.lmtId);

    return !isInCart ? (
        button
    ) : (
        <Flex.Row gap="small" className="catalog__cart-button--added">
            <Icon.Check.Medium.P50 />
            <Label.Small.SemiBold.N50>{addedText}</Label.Small.SemiBold.N50>
        </Flex.Row>
    );
}

function HeaderActions(props: {
    context: CatalogContext;
    standalone: boolean;
    onAddToCatalog: () => void;
    onAddOneOffBooking: () => void;
    onClose: () => void;
}) {
    const { onAddToCatalog, onAddOneOffBooking, context, standalone, onClose } = props;

    const [loading, setLoading] = useState<boolean>(false);
    const options: MenuButtonOptions = [
        {
            label: "Catalog item",
            subLabel: "Create a new item to catalog. Can be reused and published.",
            icon: "ImportContacts",
            onClick: onAddToCatalog,
        },
        {
            label: "One-off booking",
            subLabel: "Unique, one-time use. Will not be saved to catalog.",
            icon: "CalendarToday",
            onClick: () => {
                setLoading(true);
                onAddOneOffBooking();
            },
        },
    ];

    return (
        <Flex.Row gap="large">
            {isRequestContext(context) ? (
                <>
                    {context === "service" && (
                        <HeaderActionButtons
                            onAddToCatalog={onAddToCatalog}
                            loading={loading}
                            menuOptions={options}
                        />
                    )}
                    <Button.Ghost
                        onClick={() => {
                            onClose();
                        }}
                    >
                        <Icon.Close.Medium.N10 />
                    </Button.Ghost>
                </>
            ) : (
                <>
                    {context === "browse" && (
                        <HeaderActionButtons
                            onAddToCatalog={onAddToCatalog}
                            loading={loading}
                        />
                    )}

                    {!standalone && (
                        <Button.Ghost
                            onClick={() => {
                                onClose();
                            }}
                        >
                            <Icon.Close.Medium.N10 />
                        </Button.Ghost>
                    )}
                </>
            )}
        </Flex.Row>
    );
}

function HeaderActionButtons(props: {
    onAddToCatalog: () => void;
    loading: boolean;
    menuOptions?: MenuButtonOptions;
}) {
    const { onAddToCatalog, loading, menuOptions } = props;
    const { urlState, setUrlState } = useCatalogSearchUrlParams();

    const searchedByLocation = !!urlState.locationSearchTerm;

    function toggleView() {
        setUrlState({
            ...urlState,
            view: urlState.view === "list" ? "map" : "list",
        });
    }

    return (
        <>
            <Tooltip
                label="Search by location first"
                placement="bottom"
                hide={searchedByLocation}
            >
                <Button.N10 disabled={!searchedByLocation} onClick={toggleView}>
                    {urlState.view === "list" ? (
                        <Icon.Map.Medium.N100 />
                    ) : (
                        <Icon.List.Medium.N100 />
                    )}
                    <Label.Medium.SemiBold.N100>
                        {urlState.view === "list" ? "Map" : "List"}
                    </Label.Medium.SemiBold.N100>
                </Button.N10>
            </Tooltip>

            {menuOptions ? (
                <MenuButton
                    options={menuOptions}
                    placement="right"
                    button={
                        <Flex.Row
                            className="catalog__add-new-button pictogram-icon--hover"
                            gap="medium"
                        >
                            <Icon.Add.Medium.P50 />
                            <Label.Medium.SemiBold.N10>
                                Add new
                            </Label.Medium.SemiBold.N10>
                            {loading && <LoadingSpinner />}
                        </Flex.Row>
                    }
                />
            ) : (
                <Button
                    className="catalog__add-new-button pictogram-icon--hover"
                    onClick={() => {
                        onAddToCatalog?.();
                    }}
                >
                    <Icon.Add.Medium.P50 />
                    <Label.Medium.SemiBold.N10>Add new</Label.Medium.SemiBold.N10>
                    {loading && <LoadingSpinner />}
                </Button>
            )}
        </>
    );
}

function Title(props: {
    context: CatalogContext;
    member: MemberDataFirestoreRecord | undefined;
    request: Request | undefined;
}) {
    const { context, member, request } = props;
    const mainTitle = contextToTitleMap[context] ?? contextToTitleMap.browse;
    const secondaryTitleParts = [
        member ? nameWithoutTitle(member) : undefined,
        request?.title,
    ].filter(excludeUndefined);

    return (
        <Flex.Row gap="small">
            <h2>{mainTitle}</h2>

            {secondaryTitleParts.map((titlePart, index) => (
                <>
                    {index === 0 && <InlineTextDelimiter />}
                    <Label.Medium.Light.N50>{titlePart}</Label.Medium.Light.N50>
                    {secondaryTitleParts[index + 1] && <InlineTextDelimiter />}
                </>
            ))}
        </Flex.Row>
    );
}

type Props = {
    className?: string;
    currentAgent: Agent;
    googlePlaceService: google.maps.places.PlacesService;
    suppliers: Supplier[];
    request: Request | undefined;
    member: MemberDataFirestoreRecord | undefined;
    cartItems: string[];
    standalone: boolean;
};

type State = {
    totalResults: number;
    cartItems: string[];
    fullsizeFiltersVisible: boolean;
    highlightedItemId: string | undefined;
};
function Catalog(props: Props) {
    const {
        className,
        currentAgent,
        googlePlaceService,
        suppliers,
        request,
        member,
        cartItems,
        standalone,
    } = props;
    const { urlState, setUrlState } = useCatalogSearchUrlParams();
    const selection = useCatalogSelectionUrlParams();
    const { state, setState } = useComponentState<State>({
        totalResults: 0,
        cartItems,
        fullsizeFiltersVisible: true,
        highlightedItemId: undefined,
    });
    const history = useHistory();
    const { isAdmin } = useClaims();
    const queryClient = useQueryClient();
    const [createPrefill, setCreatePrefill] = useState<
        CatalogCreatePrefill | undefined
    >();
    const [selectedRecommendationId, setSelectedRecommendationId] = useState<
        string | undefined
    >();

    function returnToSearch() {
        history.push(
            routes.catalogSearch(selection.urlState.context, request?.id, member?.id),
        );
    }

    function returnToSearchWithItem(item: string) {
        history.push(
            routes.catalogSearch(
                selection.urlState.context,
                request?.id,
                member?.id,
                item,
            ),
        );
    }

    function onDuplicateSelect(itemId: string) {
        if (selection.urlState.context === "recommendations") {
            clearCreatePrefill();
            goToViewMode(itemId);
            return;
        }
        returnToSearchWithItem(itemId);
    }

    function closeCatalog() {
        if (request) {
            history.push(routes.request(request.id));
        } else if (member) {
            history.push(routes.member(member.id));
        }
    }

    function goToViewMode(catalogItemId: string) {
        history.push(
            routes.catalogView(
                selection.urlState.context,
                catalogItemId,
                request?.id,
                member?.id,
            ),
        );
    }

    function goToSearchViewMode(viewableItem: CatalogItemFull) {
        history.push(
            routes.catalogSearch("browse", request?.id, member?.id, viewableItem.id),
        );
    }

    function goToCreateMode(prefill?: CatalogCreatePrefill) {
        setCreatePrefill(prefill);
        history.push(
            routes.catalogCreate(selection.urlState.context, request?.id, member?.id),
        );
    }

    function clearCreatePrefill() {
        setCreatePrefill(undefined);
    }

    function createOneOffBooking(input?: CreateOneOffBookingInput) {
        EventEmitter.emit({
            name: "createOneOffBooking",
            params: { input },
        });
    }

    function sendItemToCart(
        item: CatalogItemFull,
    ): "service" | "proactivity" | "recommendations" | undefined {
        if (isRequestContext(selection.urlState.context)) {
            if (request) {
                const recommendation =
                    viewingRecommendedItems && recommendationsCache
                        ? recommendationsCache.items.find((r) => r.id === item.id)
                        : undefined;
                const rankIndex = recommendation
                    ? recommendationsCache?.items.findIndex((r) => r.id === item.id)
                    : undefined;
                const rank =
                    rankIndex !== undefined && rankIndex >= 0
                        ? rankIndex + 1
                        : undefined;

                EventEmitter.emit({
                    name: "addCatalogCartItem",
                    params: {
                        item,
                        recommendation:
                            recommendation && rank !== undefined
                                ? {
                                      fitScore: recommendation.score,
                                      rank,
                                  }
                                : undefined,
                    },
                });

                setState({
                    cartItems: [...state.cartItems, item.id],
                });
                return selection.urlState.context;
            }
        }

        if (selection.urlState.context === "proactivity") {
            if (canBeSharedAsProactivity(item, getLocale(member?.serviceLanguage))) {
                EventEmitter.emit({
                    name: "addCatalogItemToChatAttachments",
                    params: {
                        item,
                    },
                });

                setState({
                    cartItems: [...state.cartItems, item.id],
                });
                return "proactivity";
            }
        }

        return undefined;
    }

    function onSuccessfulEdit(editedItem: CatalogItemFull) {
        setCatalogItemCache(queryClient, editedItem);

        EventEmitter.emit({
            name: "showToastMessage",
            params: {
                message: `"${editedItem.name}" successfully updated`,
                type: "info",
            },
        });

        EventEmitter.emit({
            name: "catalogItemEdited",
            params: {
                item: editedItem,
            },
        });

        if (state.cartItems.includes(editedItem.id)) {
            sendItemToCart(editedItem);
        }
    }

    function onSuccessfulCreate(item: CatalogItemFull) {
        clearCreatePrefill();
        const result = sendItemToCart(item);
        const message = result
            ? contextToSuccessfulAddition(item)[result]
            : contextToSuccessfulAddition(item).browse;

        EventEmitter.emit({
            name: "showToastMessage",
            params: {
                message,
                type: "info",
            },
        });

        goToSearchViewMode(item);
    }

    const viewingRecommendedItems = selection.urlState.context === "recommendations";

    const recommendationsCache =
        viewingRecommendedItems && request?.id
            ? queryClient.getQueryData<RequestRecommendationsResponseData>([
                  "requestRecommendations",
                  { param: { id: request.id } },
              ])
            : undefined;

    const selectedRecommendation = recommendationsCache?.items.find(
        (item) => item.id === selectedRecommendationId,
    );
    const isWebRecommendationSelected = selectedRecommendation?.source === "web";
    const activeCatalogItemId = viewingRecommendedItems
        ? isWebRecommendationSelected
            ? undefined
            : selectedRecommendationId
        : selection.urlState.searchCatalogId;

    const { data: selectedCatalogItemData, isLoading: isLoadingSelectedCatalogItem } =
        useGet(
            "catalogItemById",
            {
                param: { id: activeCatalogItemId ?? "" },
            },
            {
                enabled: !!activeCatalogItemId,
            },
        );
    const selectedItem = selectedCatalogItemData?.catalogItem;

    useEffect(() => {
        setState({ cartItems });
    }, [JSON.stringify(cartItems)]);

    const SelectedCatalogItemTitle = selectedItem ? (
        <Flex.Row className="catalog__item-actions" justifyContent="space-between">
            <CatalogItemHeader
                item={selectedItem}
                cartItems={state.cartItems}
                selectCatalogItem={(item) => {
                    setCatalogItemCache(queryClient, item);
                }}
                request={request}
                sendItemToCart={sendItemToCart}
            />
        </Flex.Row>
    ) : (
        <Flex.Row className="catalog__item-actions">
            <h3>...</h3>
        </Flex.Row>
    );

    const distanceOrigin = getDistanceOrigin(urlState.filter);

    const isDistanceSortingApplied =
        hasAllBoundingBoxParams(urlState.filter) ||
        hasAllLocationParams(urlState.filter);

    const inListView = urlState.view === "list";

    const recommendedCatalogIds = (recommendationsCache?.items ?? [])
        .filter((item) => item.source === "catalog")
        .map((item) => item.id);

    const { data: searchResults, isLoading: isLoadingSearchResults } =
        useCatalogSearchResults(urlState.filter);

    const totalResults = viewingRecommendedItems
        ? (recommendationsCache?.items.length ?? 0)
        : (searchResults?.pages?.[0].total ?? 0);

    const SelectedCatalogItem = selectedItem ? (
        <CatalogItemView
            mapId="catalog-beta_view__item-selection"
            className="catalog__item"
            googlePlaceService={googlePlaceService}
            item={selectedItem}
            suppliers={suppliers}
            isAdmin={isAdmin}
            distanceOrigin={distanceOrigin}
            recommendationFitAnalysis={
                selectedRecommendation
                    ? {
                          fitScore: selectedRecommendation.score,
                          whyRelevant:
                              selectedRecommendation.rationale?.whyRelevant ??
                              undefined,
                          bestUseCase:
                              selectedRecommendation.rationale?.bestUseCase ??
                              undefined,
                          potentialRisks:
                              selectedRecommendation.rationale?.potentialRisks ??
                              undefined,
                      }
                    : undefined
            }
            onView={(viewableItem) => {
                goToViewMode(viewableItem.id);
            }}
            onToggleArchive={(item) => {
                onSuccessfulEdit(item);
            }}
        />
    ) : (
        <Flex.Column
            className="catalog--loading"
            alignItems="center"
            justifyContent="center"
        >
            {isLoadingSelectedCatalogItem && <LoadingSpinner />}
        </Flex.Column>
    );

    useEffect(
        function selectItemAfterInitialLoad() {
            if (viewingRecommendedItems) return;
            if (isLoadingSearchResults) return;
            // Preserve deep-linked catalogId (e.g. archived items absent from search).
            if (selection.urlState.searchCatalogId) return;

            const firstPage = searchResults?.pages?.[0].data;

            if (!firstPage) return;

            const selectedId = firstPage[0]?.lmtId;
            if (!selectedId) return;

            selection.setUrlState({
                catalogId: selectedId,
            });
        },
        [
            viewingRecommendedItems,
            searchResults,
            isLoadingSearchResults,
            selection.urlState.searchCatalogId,
            selection.setUrlState,
        ],
    );

    const isRecommendationsMode = viewingRecommendedItems && !!request?.id;

    const resultsSubtitle = isRecommendationsMode
        ? "AI recommendations"
        : isDistanceSortingApplied
          ? "Nearby relevant options first"
          : "Most relevant options first";

    function setHighlightedItemId(id: string | undefined) {
        setState({ highlightedItemId: id });
    }

    const panelsContext: CatalogPanelsContextValue = {
        isRecommendationsMode,
        inListView,
        request,
        defaultSearch: urlState.filter,
        recommendedCatalogIds,
        selectedRecommendationId,
        onSelectRecommendation: setSelectedRecommendationId,
        onSelectCatalogItem: (id) => {
            selection.setUrlState({ catalogId: id });
        },
        highlightedItemId: state.highlightedItemId,
        onHover: setHighlightedItemId,
        selectedRecommendation,
        onAddToCatalog: goToCreateMode,
        onAddOneOffBooking: createOneOffBooking,
        catalogHeader: SelectedCatalogItemTitle,
        catalogBody: SelectedCatalogItem,
        selectedItem,
        cartItems: state.cartItems,
        onCartItemClick: sendItemToCart,
    };

    return (
        <>
            <CatalogPanelsContext.Provider value={panelsContext}>
                <Flex.Column className={classNames("tab", className)}>
                    <Flex.Column>
                        <Flex.Row
                            gap="medium"
                            className="catalog__title"
                            justifyContent="space-between"
                        >
                            <div>
                                <Title
                                    context={selection.urlState.context}
                                    member={member}
                                    request={request}
                                />
                            </div>

                            <div>
                                <HeaderActions
                                    context={selection.urlState.context}
                                    standalone={standalone}
                                    onAddToCatalog={() => {
                                        goToCreateMode();
                                    }}
                                    onAddOneOffBooking={() => {
                                        createOneOffBooking();
                                    }}
                                    onClose={() => {
                                        closeCatalog();
                                    }}
                                />
                            </div>
                        </Flex.Row>

                        {!viewingRecommendedItems && (
                            <HandleClicksOutside
                                className="catalog__filters-outside-click"
                                disabled={
                                    !state.fullsizeFiltersVisible ||
                                    state.totalResults === 0
                                }
                                onClickOutside={() => {
                                    // Timeout so that clicks goes through to original event
                                    setTimeout(() => {
                                        setState({ fullsizeFiltersVisible: false });
                                    }, 200);
                                }}
                            >
                                <Flex.Row
                                    className={classNames("catalog__filters", {
                                        "catalog__filters--hidden":
                                            !state.fullsizeFiltersVisible,
                                    })}
                                >
                                    <CatalogFilters
                                        defaultSearch={urlState}
                                        suppliers={suppliers}
                                        onFilterChange={setUrlState}
                                    />
                                </Flex.Row>
                            </HandleClicksOutside>
                        )}
                    </Flex.Column>

                    <Flex.Row alignItems="start" className="catalog__content">
                        <Flex.Column className="catalog__search-tab">
                            {!isRecommendationsMode &&
                                !state.fullsizeFiltersVisible && (
                                    <MinimizedCatalogFilters
                                        defaultSearch={urlState}
                                        onClick={() => {
                                            setState({ fullsizeFiltersVisible: true });
                                        }}
                                    />
                                )}

                            <Flex.Row
                                className="catalog__search-tab-total"
                                alignItems="center"
                                gap="medium"
                            >
                                <Label.Medium.SemiBold.N10>
                                    {singularPlural(
                                        `${totalResults} result`,
                                        `${totalResults} results`,
                                        totalResults,
                                    )}
                                </Label.Medium.SemiBold.N10>
                                <Label.Small.Light.N50>
                                    {resultsSubtitle}
                                </Label.Small.Light.N50>
                            </Flex.Row>

                            <CatalogListPanel />
                        </Flex.Column>

                        <Lib.Flex.Column className="catalog__selected-tab">
                            <CatalogDetailPanel />
                        </Lib.Flex.Column>
                    </Flex.Row>
                </Flex.Column>
            </CatalogPanelsContext.Provider>

            {selection.urlState.mode === "edit" && selection.urlState.modeCatalogId && (
                <EditCatalogItem
                    catalogItemId={selection.urlState.modeCatalogId}
                    currentAgent={currentAgent}
                    suppliers={suppliers}
                    onClose={() => {
                        returnToSearch();
                    }}
                    onSave={(item) => {
                        onSuccessfulEdit(item);
                    }}
                />
            )}

            {selection.urlState.mode === "create" && (
                <CatalogItemCreate
                    isAdmin={isAdmin}
                    suppliers={suppliers}
                    initialPlaceId={createPrefill?.placeId}
                    initialValues={createPrefill?.initialValues}
                    onCancel={() => {
                        clearCreatePrefill();
                        returnToSearch();
                    }}
                    onSave={(item) => {
                        onSuccessfulCreate(item);
                    }}
                    onDuplicateSelect={onDuplicateSelect}
                    defaultCatalogSource={
                        selection.urlState.context === "browse"
                            ? "albertine"
                            : "proposal"
                    }
                />
            )}

            {selection.urlState.mode === "view" && selection.urlState.modeCatalogId && (
                <ViewCatalogItem
                    catalogItemId={selection.urlState.modeCatalogId}
                    currentAgent={currentAgent}
                    googlePlaceService={googlePlaceService}
                    suppliers={suppliers}
                    distanceOrigin={distanceOrigin}
                    onBack={() => {
                        returnToSearch();
                    }}
                    onClose={() => {
                        returnToSearch();
                    }}
                    onToggleArchive={(item) => {
                        onSuccessfulEdit(item);
                    }}
                    request={request}
                    selectCatalogItem={(item) => {
                        setCatalogItemCache(queryClient, item);
                    }}
                    sendItemToCart={sendItemToCart}
                    cartItems={state.cartItems}
                />
            )}
        </>
    );
}

type CatalogCreatePrefill = {
    placeId?: string;
    initialValues?: Partial<CatalogItemFull>;
};

type CatalogPanelsContextValue = {
    isRecommendationsMode: boolean;
    inListView: boolean;
    request: Request | undefined;
    defaultSearch: SearchCatalogItemsQuery;
    recommendedCatalogIds: string[];
    selectedRecommendationId: string | undefined;
    onSelectRecommendation: (recommendationId: string) => void;
    onSelectCatalogItem: (catalogId: string) => void;
    highlightedItemId: string | undefined;
    onHover: (id: string | undefined) => void;
    selectedRecommendation: RecommendationItem | undefined;
    onAddToCatalog: (prefill?: CatalogCreatePrefill) => void;
    onAddOneOffBooking: (input?: CreateOneOffBookingInput) => void;
    catalogHeader: ReactNode;
    catalogBody: ReactNode;
    selectedItem: CatalogItemFull | undefined;
    cartItems: string[];
    onCartItemClick: (item: CatalogItemFull) => void;
};

const CatalogPanelsContext = createContext<CatalogPanelsContextValue | null>(null);

function useCatalogPanelsContext() {
    const context = useContext(CatalogPanelsContext);
    if (!context) {
        throw new Error("Catalog panel components require CatalogPanelsContext");
    }
    return context;
}

function CatalogListPanel() {
    const {
        isRecommendationsMode,
        request,
        defaultSearch,
        recommendedCatalogIds,
        selectedRecommendationId,
        onSelectRecommendation,
        onSelectCatalogItem,
        onHover,
        highlightedItemId,
        inListView,
    } = useCatalogPanelsContext();

    if (isRecommendationsMode && request) {
        return (
            <RecommendationResults
                className="catalog__search-results"
                requestId={request.id}
                defaultSearch={defaultSearch}
                recommendedCatalogIds={recommendedCatalogIds}
                selectedRecommendationId={selectedRecommendationId}
                onSelect={onSelectRecommendation}
                onHover={onHover}
                highlightedItemId={highlightedItemId}
            />
        );
    }

    return (
        <CatalogSearchResults
            className="catalog__search-results"
            defaultSearch={defaultSearch}
            onResultItemClick={onSelectCatalogItem}
            onResultItemHover={onHover}
            highlightedItemId={highlightedItemId}
            collabsible={!inListView}
        />
    );
}

function CatalogDetailPanel() {
    const {
        isRecommendationsMode,
        inListView,
        selectedRecommendation,
        onAddToCatalog,
        onAddOneOffBooking,
        catalogHeader,
        catalogBody,
        selectedItem,
        request,
        cartItems,
        onCartItemClick,
        onHover,
        highlightedItemId,
    } = useCatalogPanelsContext();

    if (isRecommendationsMode && selectedRecommendation?.source === "web") {
        return (
            <WebItemDetail
                item={selectedRecommendation}
                onAddToCatalog={onAddToCatalog}
                onAddOneOffBooking={onAddOneOffBooking}
            />
        );
    }

    if (isRecommendationsMode || inListView) {
        return (
            <>
                {catalogHeader}
                {catalogBody}
            </>
        );
    }

    return (
        <MapView
            selectedItem={selectedItem}
            request={request}
            cartItems={cartItems}
            onCartItemClick={onCartItemClick}
            onPinHover={onHover}
            highlightedItemId={highlightedItemId}
        />
    );
}

function setCatalogItemCache(queryClient: QueryClient, item: CatalogItemFull) {
    const cacheEntry = { catalogItem: item };

    queryClient.setQueryData(
        ["catalogItemById", { param: { id: item.id } }],
        cacheEntry,
    );

    if (item.lmtId !== item.id) {
        queryClient.setQueryData(
            ["catalogItemById", { param: { id: item.lmtId } }],
            cacheEntry,
        );
    }
}

export default Catalog;
